/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Switch, Form, Select } from "core/components";
import { WarehouseFormData, WarehouseSchema } from "core/schema";
import { admin_warehouses_path, admin_warehouse_path } from "routes";
import { yupResolver } from "@hookform/resolvers/yup";
import { navigateTo } from "core/navigation";

type WarehouseFormProps = {
  warehouse: WarehouseFormData;
};

const WarehouseForm = ({ warehouse, accounts }: WarehouseFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<WarehouseFormData>({
    mode: "onChange",
    defaultValues: WarehouseSchema.cast(warehouse),
    resolver: yupResolver(WarehouseSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: WarehouseFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_warehouse_path(warehouse), data);
      } else {
        await api.post(admin_warehouses_path(warehouse), data);
      }

      navigateTo(admin_warehouses_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={warehouse.id ? "Edit Warehouse" : "New Warehouse"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_warehouses_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} maxLength={100} />
                  <Controller
                    name="account_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Default GL Account"
                        placeholder="Select GL Account"
                        required
                        options={accounts}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        noOptionsMessage={() => "No GL Accounts defined."}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                        }}
                        value={accounts.find((account) => account.id == field.value) || null}
                        errorMessage={errors.account_id?.message}
                      />
                    )}
                  />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default WarehouseForm;
